import { useCallback, useEffect, useRef, useState } from 'react';
import { bomClient } from '../client/bomClient';
import { componentKeyOf } from '../components/discovery/discoveryModel';
import { useOnRobbieApplied } from '../contexts/RobbieUiContext';
import { keysForNames, pickKeysOf, serverTickedKeys, shortlistItemFor, tickedSupplierCount } from './shortlistListings';

/** The ids a BOM row may carry; Robbie's card names it by any of them. */
type RowIds = { componentId?: unknown; id?: unknown; _id?: unknown } | undefined;

export type ShortlistSaveState = 'idle' | 'saving' | 'failed';

/**
 * The buyer's shortlist on discovery: ticked product rows (listing keys)
 * keyed by component, seeded from the server's SHORTLISTED flags and
 * re-synced as research lands. Each toggle saves in the background; Continue
 * saves the whole map in the open (popup, error with the picks kept) and only
 * moves on once it has landed. Counts are suppliers: a supplier with two
 * ticked products is one supplier on the shortlist.
 */
export const useShortlistSelection = (bomId: string | undefined, components: readonly unknown[]) => {
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [saveState, setSaveState] = useState<ShortlistSaveState>('idle');
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    if (components.length === 0) return;
    setSelected((prev) => {
      const next = { ...prev };
      let changed = false;
      components.forEach((c, i) => {
        const key = componentKeyOf(c, i);
        const serverKeys = serverTickedKeys(c);
        if (serverKeys.length > 0 && (!prev[key] || prev[key].length === 0)) {
          next[key] = serverKeys;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [components]);

  // A Robbie shortlist card the buyer applied is already saved: tick exactly
  // what it saved, component by component.
  useOnRobbieApplied('shortlist', (event) => {
    setSelected((prev) => {
      const next = { ...prev };
      event.lists.forEach((list) => {
        const index = components.findIndex((c) =>
          [(c as RowIds)?.componentId, (c as RowIds)?.id, (c as RowIds)?._id].some((v) => v !== undefined && (String(v) === list.componentId || String(v) === list.componentKey))
        );
        if (index === -1) return;
        const key = componentKeyOf(components[index], index);
        next[key] = keysForNames(components[index], list.suppliers, prev[key] || []);
      });
      return next;
    });
  });

  const persist = useCallback(
    (map: Record<string, string[]>, source: 'buyer' | 'robbie' = 'buyer') => {
      if (!bomId) return Promise.resolve();
      return bomClient.shortlistSuppliersBatch(bomId, {
        components: components.map((c, i) => {
          const key = componentKeyOf(c, i);
          return shortlistItemFor(c, key, map[key] || []);
        }),
        source,
      });
    },
    [bomId, components],
  );

  const saveInBackground = (map: Record<string, string[]>, source: 'buyer' | 'robbie' = 'buyer') => {
    // A failed background save loses nothing: the picks stay selected, and
    // Continue re-saves the whole map in the open, where a failure is shown.
    persist(map, source).catch(() => undefined);
  };

  /** Ticks or unticks one product row (a listing key) on a component. */
  const toggle = (componentKey: string, listingKey: string) => {
    const list = selectedRef.current[componentKey] || [];
    const nextList = list.includes(listingKey) ? list.filter((n) => n !== listingKey) : [...list, listingKey];
    const nextMap = { ...selectedRef.current, [componentKey]: nextList };
    setSelected(nextMap);
    saveInBackground(nextMap);
  };

  /** Adds every research-ranked pick (its best product) across all components; never removes one. */
  const pickAll = () => {
    const nextMap = { ...selectedRef.current };
    components.forEach((c, i) => {
      const key = componentKeyOf(c, i);
      const picks = pickKeysOf(c);
      if (picks.length === 0) return;
      nextMap[key] = Array.from(new Set([...(nextMap[key] || []), ...picks]));
    });
    setSelected(nextMap);
    // Robbie's picks, applied by the buyer: the change log credits Robbie.
    saveInBackground(nextMap, 'robbie');
  };

  /** Resolves true once saved; false leaves the popup on its error. */
  const saveForContinue = async (): Promise<boolean> => {
    setSaveState('saving');
    try {
      await persist(selectedRef.current);
      setSaveState('idle');
      return true;
    } catch {
      setSaveState('failed');
      return false;
    }
  };

  const dismissSave = () => setSaveState('idle');

  /** Suppliers ticked on one component. */
  const pickedOn = (component: unknown, index: number): number =>
    tickedSupplierCount(component, selected[componentKeyOf(component, index)] || []);

  const perComponent = components.map(pickedOn);
  const totalPicked = perComponent.reduce((n, count) => n + count, 0);
  const componentsPicked = perComponent.filter((count) => count > 0).length;

  return {
    selected,
    toggle,
    pickAll,
    saveForContinue,
    dismissSave,
    saveState,
    pickedOn,
    totalPicked,
    componentsPicked,
  };
};

export default useShortlistSelection;
